import React, { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ChevronDown, FileDown, FileSpreadsheet, FileUp, History, Layers,
  LayoutTemplate, Link2, Loader2, Copy, Users, EyeOff,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import api from '../../lib/api';
import { downloadFile } from '../../lib/download';
import { errMsg, fmtMoney, fmtDate } from '../../lib/format';

export const ActionsMenu = ({ quotation, frozen, onImported }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [exportKind, setExportKind] = useState(null); // 'pdf' | 'excel' | null

  const runExport = (versionKey) => {
    const kind = exportKind;
    setExportKind(null);
    const suffix = versionKey === 'interna' ? '_interna' : '';
    if (kind === 'pdf') {
      doDownload(`/quotations/${id}/export-pdf?version=${versionKey}`, `${number}${suffix}.pdf`, versionKey === 'interna' ? 'PDF interno' : 'PDF cliente');
    } else if (kind === 'excel') {
      doDownload(`/quotations/${id}/export-excel?version=${versionKey}`, `${number}${suffix}.xlsx`, versionKey === 'interna' ? 'Excel interno' : 'Excel cliente');
    }
  };

  const id = quotation.id;
  const number = quotation.quotation_number || 'cotizacion';

  const doDownload = async (url, filename, label) => {
    setBusy(true);
    try {
      await downloadFile(url, filename);
      toast.success(`${label} descargado`);
    } catch (e) {
      toast.error(errMsg(e, `No se pudo descargar ${label}`));
    } finally {
      setBusy(false);
    }
  };

  const importMutation = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return (await api.post(`/quotations/${id}/import-excel`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
    },
    onSuccess: () => {
      toast.success('Excel importado: estructura actualizada');
      if (onImported) onImported();
    },
    onError: (e) => toast.error(errMsg(e, 'Error al importar Excel')),
  });

  const saveVersionMutation = useMutation({
    mutationFn: async () => (await api.post(`/quotations/${id}/save-version`)).data,
    onSuccess: (d) => toast.success(`Versión ${d.version} guardada`),
    onError: (e) => toast.error(errMsg(e)),
  });

  const saveTemplateMutation = useMutation({
    mutationFn: async () => (await api.post(`/quotations/${id}/save-as-template`, { nombre: templateName || null })).data,
    onSuccess: (d) => { toast.success(`Plantilla “${d.nombre}” guardada`); setTemplateOpen(false); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const shareMutation = useMutation({
    mutationFn: async () => (await api.post(`/quotations/${id}/generate-share-link`)).data,
    onSuccess: async (d) => {
      const url = `${window.location.origin}/portal/${d.token}`;
      setShareUrl(url);
      setShareOpen(true);
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Enlace copiado al portapapeles');
      } catch {
        toast.info('Enlace generado');
      }
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const { data: versions, isLoading: loadingVersions } = useQuery({
    queryKey: ['quotation-versions', id],
    queryFn: async () => (await api.get(`/quotations/${id}/versions`)).data,
    enabled: versionsOpen,
  });

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx"
        className="hidden"
        data-testid="import-excel-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) importMutation.mutate(f);
          e.target.value = '';
        }}
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8" data-testid="editor-actions-menu" disabled={busy || importMutation.isPending}>
            {(busy || importMutation.isPending) ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
            Acciones <ChevronDown size={14} className="ml-1.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel>Documentos</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => setExportKind('pdf')} data-testid="export-pdf-button">
            <FileDown size={14} className="mr-2" /> Exportar PDF
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setExportKind('excel')} data-testid="export-excel-button">
            <FileSpreadsheet size={14} className="mr-2" /> Exportar Excel
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => doDownload(`/quotations/${id}/insumos/export-excel`, `insumos_${number}.xlsx`, 'Explosión de insumos')} data-testid="export-insumos-button">
            <Layers size={14} className="mr-2" /> Explosión de insumos
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => fileRef.current?.click()} disabled={frozen} data-testid="import-excel-button">
            <FileUp size={14} className="mr-2" /> Importar Excel
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Versiones y plantillas</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => saveVersionMutation.mutate()} data-testid="save-version-button">
            <History size={14} className="mr-2" /> Guardar versión
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setVersionsOpen(true)} data-testid="view-versions-button">
            <History size={14} className="mr-2" /> Ver versiones
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => { setTemplateName(''); setTemplateOpen(true); }} data-testid="save-template-button">
            <LayoutTemplate size={14} className="mr-2" /> Guardar como plantilla
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Compartir</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => shareMutation.mutate()} data-testid="share-link-button">
            <Link2 size={14} className="mr-2" /> Enlace para cliente
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Diálogo guardar plantilla */}
      <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
        <DialogContent className="max-w-md" data-testid="save-template-dialog">
          <DialogHeader><DialogTitle>Guardar como plantilla</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>Nombre de la plantilla</Label>
            <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder={`Plantilla · ${quotation.nombre || ''}`} data-testid="template-name-input" />
            <p className="text-[12px] text-muted-foreground">Se guardará una copia de la estructura actual (grupos, conceptos y APU).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveTemplateMutation.mutate()} disabled={saveTemplateMutation.isPending} data-testid="confirm-save-template-button">Guardar plantilla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo versiones */}
      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="max-w-lg" data-testid="versions-dialog">
          <DialogHeader><DialogTitle>Versiones guardadas</DialogTitle></DialogHeader>
          {loadingVersions ? (
            <div className="py-6 text-center text-muted-foreground text-sm">Cargando…</div>
          ) : (versions || []).length === 0 ? (
            <div className="py-6 text-center text-muted-foreground text-sm" data-testid="versions-empty">Aún no hay versiones. Usa “Guardar versión” para crear un punto de restauración histórico.</div>
          ) : (
            <Table data-testid="versions-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="th-label">Versión</TableHead>
                  <TableHead className="th-label">Fecha</TableHead>
                  <TableHead className="th-label">Creada por</TableHead>
                  <TableHead className="th-label text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(versions || []).map((v) => (
                  <TableRow key={v.id} className="hover:bg-background/40" data-testid={`version-row-${v.version}`}>
                    <TableCell className="font-mono">v{v.version}</TableCell>
                    <TableCell className="text-muted-foreground text-[13px]">{fmtDate(v.created_at)}</TableCell>
                    <TableCell className="text-[13px]">{v.created_by || '—'}</TableCell>
                    <TableCell className="money">{fmtMoney(v.total, quotation.moneda)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo enlace compartido */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md" data-testid="share-link-dialog">
          <DialogHeader><DialogTitle>Enlace para cliente</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Cualquier persona con este enlace podrá ver la cotización en modo lectura (sin desglose interno de APU).</p>
          <div className="flex gap-2">
            <Input readOnly value={shareUrl} className="font-mono text-[12px]" data-testid="share-url-input" />
            <Button variant="outline" size="icon" onClick={copyShare} data-testid="copy-share-url-button"><Copy size={15} /></Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo de versión de exportación */}
      <Dialog open={!!exportKind} onOpenChange={(v) => { if (!v) setExportKind(null); }}>
        <DialogContent className="max-w-md" data-testid="export-version-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Exportar {exportKind === 'pdf' ? 'PDF' : 'Excel'} · ¿Qué versión necesitas?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Button className="w-full justify-start h-auto py-3" variant="outline" onClick={() => runExport('cliente')} data-testid="export-version-cliente">
              <Users size={15} className="mr-2.5 shrink-0" />
              <span className="text-left">
                <span className="block font-medium">Versión cliente</span>
                <span className="block text-[12px] text-muted-foreground font-normal">Conceptos con precio unitario y totales, sin desglose interno</span>
              </span>
            </Button>
            <Button className="w-full justify-start h-auto py-3" variant="outline" onClick={() => runExport('interna')} data-testid="export-version-interna">
              <EyeOff size={15} className="mr-2.5 shrink-0" />
              <span className="text-left">
                <span className="block font-medium">Versión interna</span>
                <span className="block text-[12px] text-muted-foreground font-normal">Incluye detalle de APU por concepto y explosión de insumos al final</span>
              </span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
